import { useCallback, useEffect, useRef, useState } from 'react';

const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

const pickSupportedMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const candidate of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      return candidate;
    }
  }
  return '';
};

/**
 * Recording-mode states:
 *   "idle"      → not recording, nothing buffered
 *   "recording" → MediaRecorder is collecting chunks
 *   "stopping"  → stop() called, waiting for final dataavailable event
 *   "error"     → permission denied / MediaRecorder unsupported
 */
export const useAudioRecorder = () => {
  const [state, setState] = useState('idle');
  const [durationMs, setDurationMs] = useState(0);
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const intervalRef = useRef(null);
  const stopResolverRef = useRef(null);

  const isSupported = typeof window !== 'undefined'
    && typeof window.MediaRecorder !== 'undefined'
    && !!navigator?.mediaDevices?.getUserMedia;

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    startedAtRef.current = 0;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      const err = new Error('MediaRecorder is not supported in this browser');
      err.code = 'UNSUPPORTED';
      setError(err);
      setState('error');
      throw err;
    }
    if (state === 'recording' || state === 'stopping') {
      return;
    }

    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickSupportedMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const finalMime = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: finalMime });
        const finalDurationMs = startedAtRef.current
          ? Date.now() - startedAtRef.current
          : durationMs;
        const resolver = stopResolverRef.current;
        stopResolverRef.current = null;
        cleanup();
        setState('idle');
        setDurationMs(0);
        if (resolver) {
          resolver({ blob, mimeType: finalMime, durationMs: finalDurationMs });
        }
      };

      startedAtRef.current = Date.now();
      setDurationMs(0);
      intervalRef.current = window.setInterval(() => {
        setDurationMs(Date.now() - startedAtRef.current);
      }, 200);

      recorder.start();
      setState('recording');
    } catch (err) {
      cleanup();
      // Always wrap into a fresh Error: DOMException's `code` is a read-only
      // getter, so reusing it would silently drop our PERMISSION_DENIED tag.
      const wrapped = new Error(err?.message || String(err));
      wrapped.name = err?.name || 'Error';
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        wrapped.reason = 'PERMISSION_DENIED';
      }
      setError(wrapped);
      setState('error');
      throw wrapped;
    }
  }, [cleanup, durationMs, isSupported, state]);

  /**
   * Stops the recorder and resolves with the final blob.
   * Returns null when there is nothing to stop.
   */
  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || state !== 'recording') {
      return Promise.resolve(null);
    }
    setState('stopping');
    return new Promise((resolve) => {
      stopResolverRef.current = resolve;
      try {
        recorder.stop();
      } catch (err) {
        stopResolverRef.current = null;
        cleanup();
        setState('idle');
        setDurationMs(0);
        resolve(null);
      }
    });
  }, [cleanup, state]);

  /**
   * Discards the in-progress recording without producing a blob.
   */
  const cancelRecording = useCallback(() => {
    if (!recorderRef.current) return;
    try {
      // Replace onstop so the cancelled blob is discarded instead of returned.
      recorderRef.current.onstop = () => {
        cleanup();
        setState('idle');
        setDurationMs(0);
      };
      if (recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      } else {
        cleanup();
        setState('idle');
        setDurationMs(0);
      }
    } catch (err) {
      cleanup();
      setState('idle');
      setDurationMs(0);
    }
  }, [cleanup]);

  return {
    isSupported,
    isRecording: state === 'recording',
    isStopping: state === 'stopping',
    state,
    durationMs,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
  };
};

export default useAudioRecorder;
